/**
 * Hook for package build management
 *
 * Provides hook to trigger package build, poll status, and download.
 */

import { useCallback, useState } from 'react'
import type { PackageBuildStatus } from '../services/collection-packages'
import { buildPackage, downloadPackage } from '../services/collection-packages'
import { useStatusPolling } from './use-package-build/use-status-polling'

interface UsePackageBuildResult {
  status: PackageBuildStatus | null
  building: boolean
  downloading: boolean
  downloadComplete: boolean
  error: string | null
  build: (caseId: string, requestId: string, collectorOnly?: boolean) => Promise<boolean>
  download: (caseId: string, requestId: string) => Promise<boolean>
  pollStatus: (caseId: string, requestId: string) => void
  stopPolling: () => void
  clearError: () => void
}

export function usePackageBuild(): UsePackageBuildResult {
  const [status, setStatus] = useState<PackageBuildStatus | null>(null)
  const [building, setBuilding] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadComplete, setDownloadComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { pollStatus, stopPolling } = useStatusPolling(setStatus)

  const build = useCallback(
    async (caseId: string, requestId: string, collectorOnly = false): Promise<boolean> => {
      setBuilding(true)
      setError(null)
      setDownloadComplete(false)
      try {
        await buildPackage(caseId, requestId, collectorOnly)
        pollStatus(caseId, requestId)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start package build')
        return false
      } finally {
        setBuilding(false)
      }
    },
    [pollStatus]
  )

  const download = useCallback(async (caseId: string, requestId: string): Promise<boolean> => {
    setDownloading(true)
    setError(null)
    try {
      await downloadPackage(caseId, requestId)
      setDownloadComplete(true)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download package')
      return false
    } finally {
      setDownloading(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    status,
    building,
    downloading,
    downloadComplete,
    error,
    build,
    download,
    pollStatus,
    stopPolling,
    clearError,
  }
}
